// Cache owner fence (§11c). Every TanStack Query hash is prefixed with the signed-in user's id, so data
// fetched for one account can never be read by another, even when a component's query key forgot to
// include the user or business. Switching owner also removes the old owner's queries and cancels their
// in-flight fetches, so a late response cannot repopulate the cache for the next account.
import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

/** Keys that are not user data: shared by every owner (the session itself and public branding). */
const UNSCOPED = new Set(['me', 'branding']);
export const ANON = 'anon';

let owner = ANON;
let epoch = 0;

export const getCacheOwner = () => owner;
/** Increments on every owner change; long-running work captures it and stops when it changes. */
export const getOwnerEpoch = () => epoch;

export function ownerKeyHash(queryKey: QueryKey): string {
  const head = queryKey[0];
  if (typeof head === 'string' && UNSCOPED.has(head)) return hashKey(queryKey);
  return hashKey([`owner:${owner}`, ...queryKey]);
}

/**
 * Sets the cache owner. Returns true when it changed. Safe to call during render (idempotent): the hash
 * function must see the new owner before child queries build their hashes in the same render pass.
 */
export function setCacheOwner(next: string | null | undefined): boolean {
  const n = next || ANON;
  if (n === owner) return false;
  owner = n;
  epoch++;
  return true;
}

/** Removes and cancels every query that does not belong to the current owner. */
export function fenceQueries(qc: QueryClient): void {
  const prefix = hashKey([`owner:${owner}`]).slice(0, -1); // '["owner:<id>"' without the closing bracket
  const stale = (hash: string, key: QueryKey) => {
    const head = key[0];
    if (typeof head === 'string' && UNSCOPED.has(head)) return false;
    return !hash.startsWith(prefix + ',') && hash !== prefix + ']';
  };
  const cache = qc.getQueryCache();
  for (const q of cache.getAll()) {
    if (stale(q.queryHash, q.queryKey)) {
      void q.cancel({ silent: true });
      cache.remove(q);
    }
  }
}
